import { Injectable } from '@angular/core';
import { accumulatedProfitAndLoss, ItemNameAndStatus } from '../../utils/common/utilityClasses';
import { FinancialStatementItem } from '../../models/common/FinancialStatementItem';
import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { FinancialStatementPart } from '../../models/common/FinancialStatementPart';
import { FinancialStatementService } from '../common/financial-statement.service';
import { FinancialStatement } from '../../models/common/FinancialStatement';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ObjectForNameModal, UnallocatedFSData } from '../../utils/assembly/utils';
import { FsSums } from '../../utils/common/FsSums';

@Injectable()
export class AssemblyService {
  constructor(private fsService: FinancialStatementService) { }

  adjustOrderNumbersInRange(items: FinancialStatementItem[],
    startOrder: number, endOrder: number, adjustment: number): FinancialStatementItem[] {
    items.forEach((item) => {
      if (item.orderNumber >= startOrder && item.orderNumber <= endOrder) {
        item.orderNumber += adjustment;
      }
    });
    return items;
  }

  updateUnallocatedChecks(isPL: boolean, unallocatedFSData: UnallocatedFSData): void {
    unallocatedFSData.isUnallocatedCheck = unallocatedFSData.hasSums();
    this.tooltipUpdate(isPL, unallocatedFSData);
  }

  filterPLAccounts(accounts: FinancialStatementAccount[], isPL: boolean): FinancialStatementAccount[] {
    if (isPL) {
      return accounts.filter((account) => (account.code.startsWith('4') || account.code.startsWith('5')));
    }
    return accounts.filter((account) => !(account.code.startsWith('4') || account.code.startsWith('5')));
  }

  // for each allocated account that was loaded, set selected (allocated) status to true and remove from unallocated
  removeAllSelectedFromUnallocated(parts: FinancialStatementPart[], unallocatedFSData: UnallocatedFSData): void {
    if (!parts) {
      return;
    }
    parts.forEach((part) => {
      if (part.financialStatementItems) {
        part.financialStatementItems.forEach((item) => {
          item.financialStatementAccounts.forEach((account) => {
            // removes duplicates
            unallocatedFSData.accounts = unallocatedFSData.accounts.filter((accountTemp) =>
              accountTemp.id !== account.id);
            account.isSelected = true;
          });
        });
      }
    });
  }

  initUnallocatedFSData(): UnallocatedFSData {
    const tooltip = { showCondition: true, xShift: '2em' };
    return new UnallocatedFSData([], new FsSums(), tooltip, false);
  }

  async storeNewItem(itemNameAndStatus: ItemNameAndStatus, partToAddTo: FinancialStatementPart, fs: FinancialStatement): Promise<void> {
    let item = this.initializeNewItem(itemNameAndStatus, partToAddTo);

    if (item.partName === 'Partners capital') {
      this.ensureAccumulatedPlIsLast(item, partToAddTo);
    }

    item = await this.fsService.saveNewItem(item);
    partToAddTo.financialStatementItems.push(item);
    fs.items.push(item);
    partToAddTo.sortItemsByOrder();
  }

  updateItem(itemNameAndStatus: ItemNameAndStatus, item: FinancialStatementItem): void {
    item.name = itemNameAndStatus.name;
    item.assetsAndLiabilities = itemNameAndStatus.isAssetsAndLiabilities;
    this.fsService.updateItem(item);
  }

  handleAccountEdit(selectedAccounts: FinancialStatementAccount[], item: FinancialStatementItem, unallocatedData: UnallocatedFSData): void {
    // find unselected accounts and decouple from item
    const unselectedAccounts = item.financialStatementAccounts.filter((account) =>
      !selectedAccounts.some((selectedAccount) => selectedAccount.id === account.id));

    // find newly allocated accounts and couple with item
    const newSelectedAccounts = selectedAccounts.filter((account) =>
      !item.financialStatementAccounts.some((prevSelectedAccount) => prevSelectedAccount.id === account.id));

    item.financialStatementAccounts = selectedAccounts;

    // resolve unallocated
    unallocatedData.accounts = unallocatedData.accounts.filter((account) =>
      !newSelectedAccounts.some((newSelectedAccount) => newSelectedAccount.id === account.id))
      .concat(unselectedAccounts);

    this.sortAccounts(item.financialStatementAccounts, unallocatedData);
    if (item.name !== accumulatedProfitAndLoss) {
      void this.fsService.updateItem(item);
    }
  }

  initObjectForItemModal(isPL: boolean, fsItems: FinancialStatementItem[], part: FinancialStatementPart,
    item?: FinancialStatementItem): ObjectForNameModal {

    const existingNamesForItems = fsItems?.map(
      (fsItem) => fsItem.name) || [];

    return {
      isNew: !item,
      name: item?.name,
      isAssetsAndLiabilities: item?.assetsAndLiabilities,
      isPL,
      partToAddTo: part,
      existingNames: existingNamesForItems,
    };
  }

  handleDragAndDrop(event: CdkDragDrop<string[]>, part: FinancialStatementPart): void {
    if (part.partName === 'Partners capital' && event.currentIndex === part.financialStatementItems.length - 1) {
      event.currentIndex -= 1;
    }

    if ((event.previousIndex === event.currentIndex) || event.previousContainer !== event.container) {
      return;
    }

    moveItemInArray(part.financialStatementItems, event.previousIndex, event.currentIndex);
    const changedItem = part.financialStatementItems.find((item) => item.orderNumber === event.previousIndex);

    // if the item was moved up
    if (event.currentIndex < event.previousIndex) {
      this.adjustOrderNumbersInRange(part.financialStatementItems, event.currentIndex, event.previousIndex - 1, 1);
    } else { // if the item was moved down
      this.adjustOrderNumbersInRange(part.financialStatementItems, event.previousIndex + 1, event.currentIndex, -1);
    }
    changedItem.orderNumber = event.currentIndex;

    void this.fsService.updateItem(changedItem);
  }

  private initializeNewItem(itemNameAndStatus: ItemNameAndStatus, partToAddTo: FinancialStatementPart): FinancialStatementItem {
    const item = new FinancialStatementItem();
    item.partName = partToAddTo.partName;
    item.financialStatementAccounts = [];
    item.orderNumber = partToAddTo.financialStatementItems.length;
    item.name = itemNameAndStatus.name;
    item.assetsAndLiabilities = itemNameAndStatus.isAssetsAndLiabilities;
    return item;
  }

  // sort accounts in ascending order by account code as it may get mixed up with reallocations, provides consistent order for UI ease
  private sortAccounts(accounts: FinancialStatementAccount[], unallocatedFSData: UnallocatedFSData): void {
    accounts.sort((a, b) => a.code.localeCompare(b.code));
    unallocatedFSData.accounts.sort((a, b) => a.code.localeCompare(b.code));
  }

  private ensureAccumulatedPlIsLast(item: FinancialStatementItem, part: FinancialStatementPart): void {
    const accumulatedPLItem = part.financialStatementItems[part.financialStatementItems.length - 1];
    [accumulatedPLItem.orderNumber, item.orderNumber] = [item.orderNumber, accumulatedPLItem.orderNumber];
    this.fsService.updateItem(accumulatedPLItem, false);
  }

  /* eslint-disable no-nested-ternary */
  private tooltipUpdate(isPL: boolean, unallocatedFSData: UnallocatedFSData): void {
    const allocateAccounts = 'Please allocate all accounts.';
    const accountsAllocated = 'All accounts allocated. Great job!';

    unallocatedFSData.unallocatedTooltip.text = unallocatedFSData.accounts?.length ? allocateAccounts : accountsAllocated;
    unallocatedFSData.unallocatedTooltip.id = isPL ? 'plUnallocatedTooltip' : 'bsUnallocatedTooltip';
  }
  /* eslint-enable no-nested-ternary */
}
